import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useHospitalUnits, saveHospitalUnit, useAuthToken } from "@/lib/hooks";
import type { HospitalUnit, EmrStatus } from "@/lib/api";
import { downloadXlsx, stampedName } from "@/lib/exportXlsx";
import DownloadButton from "@/components/DownloadButton";

const UNIT_KEYS: (keyof HospitalUnit)[] = [
  "cv_icu", "micu", "sicu_trauma", "mix_icu", "burn_icu", "neuro_icu",
  "pcu", "picu", "transplant", "or_unit", "mother_child", "intermed_icu", "er",
];
const UNIT_LABELS: Record<string, string> = {
  cv_icu: "CV ICU", micu: "MICU", sicu_trauma: "SICU/T", mix_icu: "Mix ICU",
  burn_icu: "Burn", neuro_icu: "Neuro", pcu: "PCU", picu: "PICU",
  transplant: "Transplant", or_unit: "OR", mother_child: "M&C",
  intermed_icu: "Int. ICU", er: "ER",
};
const EMR_BADGE: Record<string, string> = {
  Yes: "bg-success/10 text-success",
  No: "bg-ink-100 text-ink-500",
  "In progress": "bg-amber-100 text-amber-700",
};

// ── Edit Modal ──────────────────────────────────────────────────────────────

function EditModal({
  hospital,
  onClose,
  onSaved,
}: {
  hospital: HospitalUnit;
  onClose: () => void;
  onSaved: (updated: HospitalUnit) => void;
}) {
  const token = useAuthToken();
  const [draft, setDraft] = useState<HospitalUnit>({ ...hospital });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const setField = (key: keyof HospitalUnit, value: unknown) =>
    setDraft((d) => ({ ...d, [key]: value }));

  async function handleSave() {
    setSaving(true);
    setErr(null);
    try {
      await saveHospitalUnit(token, draft.id, draft);
      onSaved(draft);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-xl rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-start justify-between">
          <h2 className="text-base font-semibold text-ink-900">{hospital.hospital_name}</h2>
          <button type="button" onClick={onClose} className="ml-4 text-ink-400 hover:text-ink-700">
            ✕
          </button>
        </div>

        {err && <p className="mb-4 text-sm text-danger">{err}</p>}

        <div className="space-y-4">
          {/* Hospital units */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">Hospital Units</p>
            <div className="grid grid-cols-3 gap-x-4 gap-y-2">
              {UNIT_KEYS.map((k) => (
                <label key={String(k)} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!draft[k]}
                    onChange={(e) => setField(k, e.target.checked)}
                    className="h-4 w-4 rounded border-ink-300 text-accent-600"
                  />
                  {UNIT_LABELS[String(k)]}
                </label>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Category</label>
            <select
              value={draft.category ?? ""}
              onChange={(e) => setField("category", e.target.value || null)}
              className="w-full rounded-md border border-ink-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
            >
              <option value="">—</option>
              <option value="Subscription">Subscription</option>
              <option value="Evaluation">Evaluation</option>
              <option value="Retired">Retired</option>
            </select>
          </div>

          {/* EMR + Vendor row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">EMR Connected?</label>
              <select
                value={draft.emr_connected ?? ""}
                onChange={(e) =>
                  setField("emr_connected", (e.target.value || null) as EmrStatus | null)
                }
                className="w-full rounded-md border border-ink-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
              >
                <option value="">—</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="In progress">In progress</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Vendor</label>
              <input
                type="text"
                value={draft.vendor ?? ""}
                onChange={(e) => setField("vendor", e.target.value || null)}
                className="w-full rounded-md border border-ink-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
              />
            </div>
          </div>

          {/* Last Visit */}
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Last Visit</label>
            <input
              type="text"
              value={draft.last_visit ?? ""}
              onChange={(e) => setField("last_visit", e.target.value || null)}
              placeholder="e.g. 2026-06-25 or 5/21-22/2026"
              className="w-full rounded-md border border-ink-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
            />
          </div>

          {/* Comments */}
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Comments</label>
            <textarea
              rows={3}
              value={draft.comments ?? ""}
              onChange={(e) => setField("comments", e.target.value || null)}
              className="w-full rounded-md border border-ink-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ink-200 px-4 py-1.5 text-sm font-medium text-ink-600 hover:border-ink-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-ink-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-ink-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function HospitalUnits() {
  const { data, isLoading, error } = useHospitalUnits();
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [filterUnits, setFilterUnits] = useState<Set<string>>(new Set());
  const [filterEmr, setFilterEmr] = useState<string>("");
  const [filterVendor, setFilterVendor] = useState<string>("");
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [editing, setEditing] = useState<HospitalUnit | null>(null);

  const hospitals = data?.hospitals ?? [];

  const vendors = useMemo(() => {
    const s = new Set<string>();
    for (const h of hospitals) if (h.vendor) s.add(h.vendor);
    return Array.from(s).sort();
  }, [hospitals]);

  const filtered = useMemo(() => {
    let list = hospitals;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((h) => h.hospital_name.toLowerCase().includes(q));
    }
    if (filterUnits.size > 0) {
      list = list.filter((h) =>
        Array.from(filterUnits).every((k) => h[k as keyof HospitalUnit] === true)
      );
    }
    if (filterEmr) {
      if (filterEmr === "none") {
        list = list.filter((h) => !h.emr_connected);
      } else {
        list = list.filter((h) => h.emr_connected === filterEmr);
      }
    }
    if (filterVendor) {
      list = list.filter((h) => h.vendor === filterVendor);
    }
    if (filterCategory) {
      list = list.filter((h) => h.category === filterCategory);
    }
    return list;
  }, [hospitals, search, filterUnits, filterEmr, filterVendor, filterCategory]);

  function toggleUnitFilter(key: string) {
    setFilterUnits((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleSaved(_updated: HospitalUnit) {
    qc.invalidateQueries({ queryKey: ["hospital-units"] });
    setEditing(null);
  }

  function exportXlsx() {
    const headers = [
      "Hospital", "Category", "EMR Connected", "Vendor", "Last Visit",
      ...UNIT_KEYS.map((k) => UNIT_LABELS[String(k)]),
      "Comments",
    ];
    const body = filtered.map((h) => [
      h.hospital_name,
      h.category ?? "",
      h.emr_connected ?? "",
      h.vendor ?? "",
      h.last_visit ?? "",
      ...UNIT_KEYS.map((k) => (h[k] ? "Y" : "")),
      h.comments ?? "",
    ]);
    downloadXlsx(stampedName("hospital-units"), headers, body);
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Where is My Accuryn</h1>
          <p className="mt-1 text-sm text-ink-500">
            Hospital units and access details for Accuryn accounts.
            {!isLoading && hospitals.length > 0 && (
              <> {hospitals.length} accounts · {filtered.length} shown.</>
            )}
          </p>
        </div>
        <DownloadButton onClick={exportXlsx} disabled={isLoading || !filtered.length} />
      </header>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search hospitals…"
          className="rounded-md border border-ink-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
        />
        <select
          value={filterEmr}
          onChange={(e) => setFilterEmr(e.target.value)}
          className="rounded-md border border-ink-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
        >
          <option value="">All EMR status</option>
          <option value="Yes">EMR: Yes</option>
          <option value="In progress">EMR: In progress</option>
          <option value="No">EMR: No</option>
          <option value="none">EMR: Not set</option>
        </select>
        <select
          value={filterVendor}
          onChange={(e) => setFilterVendor(e.target.value)}
          className="rounded-md border border-ink-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
        >
          <option value="">All vendors</option>
          {vendors.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="rounded-md border border-ink-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
        >
          <option value="">All categories</option>
          <option value="Subscription">Subscription</option>
          <option value="Evaluation">Evaluation</option>
          <option value="Retired">Retired</option>
        </select>
        {(search || filterUnits.size > 0 || filterEmr || filterVendor || filterCategory) && (
          <button
            type="button"
            onClick={() => { setSearch(""); setFilterUnits(new Set()); setFilterEmr(""); setFilterVendor(""); setFilterCategory(""); }}
            className="rounded-md border border-ink-200 px-2.5 py-1.5 text-sm text-ink-500 hover:border-ink-300"
          >
            Clear
          </button>
        )}
      </div>

      {/* Unit checkboxes */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-ink-200 bg-ink-50/50 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-500">Units</span>
        {UNIT_KEYS.map((k) => (
          <label key={String(k)} className="flex cursor-pointer items-center gap-1.5 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={filterUnits.has(String(k))}
              onChange={() => toggleUnitFilter(String(k))}
              className="h-4 w-4 rounded border-ink-300 text-accent-600"
            />
            {UNIT_LABELS[String(k)]}
          </label>
        ))}
      </div>

      {error && (
        <div className="card mb-4 p-4 text-sm text-danger">{(error as Error).message}</div>
      )}

      <section className="card overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-10 text-center text-sm text-ink-500">Loading…</div>
          ) : hospitals.length === 0 ? (
            <div className="p-10 text-center text-sm text-ink-500">
              No data yet. Run <code>db/hospital_units_import.py</code> to import from Excel.
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-ink-500">No hospitals match the current filters.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-ink-50 text-ink-500">
                <tr className="align-bottom">
                  <th className="w-56 px-3 py-2 text-left font-medium text-xs uppercase tracking-wide whitespace-nowrap">Account</th>
                  <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide whitespace-nowrap">Category</th>
                  {UNIT_KEYS.map((k) => (
                    <th key={String(k)} className="w-7 px-1 pb-2 pt-8 text-center font-medium">
                      <span
                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                        className="block text-xs uppercase tracking-wide whitespace-nowrap"
                      >
                        {UNIT_LABELS[String(k)]}
                      </span>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide whitespace-nowrap">EMR</th>
                  <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide whitespace-nowrap">Vendor</th>
                  <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide whitespace-nowrap">Last Visit</th>
                  <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide">Comments</th>
                  <th className="w-16 pl-3 pr-5 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {filtered.map((h) => {
                  return (
                    <tr key={h.id} className="hover:bg-ink-50">
                      <td className="w-56 max-w-[224px] px-3 py-1.5 font-medium text-ink-900">
                        {h.customer_id ? (
                          <Link
                            to={`/customers/${encodeURIComponent(h.customer_id)}`}
                            className="block truncate hover:text-accent-600"
                            title={h.hospital_name}
                          >
                            {h.hospital_name}
                          </Link>
                        ) : (
                          <span className="block truncate" title={h.hospital_name}>
                            {h.hospital_name}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-ink-600 whitespace-nowrap">{h.category ?? "—"}</td>
                      {UNIT_KEYS.map((k) => (
                        <td key={String(k)} className="w-7 px-1 py-1.5 text-center">
                          {h[k] ? (
                            <span className="text-accent-600 font-bold">✓</span>
                          ) : (
                            <span className="text-ink-200">·</span>
                          )}
                        </td>
                      ))}
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {h.emr_connected ? (
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${EMR_BADGE[h.emr_connected] ?? "bg-ink-100 text-ink-500"}`}
                          >
                            {h.emr_connected}
                          </span>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-ink-600 whitespace-nowrap">{h.vendor ?? "—"}</td>
                      <td className="px-3 py-1.5 text-ink-600 whitespace-nowrap">{h.last_visit ?? "—"}</td>
                      <td className="px-3 py-1.5 text-ink-500 max-w-[160px]">
                        {h.comments ? (
                          <span title={h.comments} className="block truncate">
                            {h.comments}
                          </span>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td className="w-16 pl-3 pr-5 py-1.5 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setEditing(h)}
                          className="rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-600 hover:border-ink-300 hover:text-ink-900"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {editing && (
        <EditModal
          hospital={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
